import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Alert, Button, Stack, Typography,
} from "@mui/material";
import { Output } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";

import { MindsetInsightSkeleton } from "./MindsetInsightSkeleton";
import { MindsetSegmentCard, useActivePage } from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { InsightLayout } from "@/components/layouts";
import { MindsetPenPortraitView } from "@/components/molecules";
import { InsightMetaProvider } from "@/context";
import { useInsight, useSegmentSelection } from "@/hooks";
import { TabOption } from "@/types";
import { getAudienceBreadcrumb } from "@/utils";


const MindsetInsightPage: React.FC = () => {

  let { audienceId } = useParams<{ audienceId: string }>();
  const { audience, insight, loading, error } = useInsight(audienceId);
  const navigate = useNavigate();

  const segmentSelection = useSegmentSelection(audience);
  const { segment: selectedSegment, index: segmentNum } = segmentSelection;
  const handleSegmentSelect = segmentSelection.select;

  const mindsetInsightTab: TabOption = {
    showScores: true,
    showLevels: false,
    showSearch: false,
    showSorts: false,
    showDownload: false,
  };

  const handleUpdateDescription = async (text: string) => {
    return Promise.resolve();
  };

  const handleRefreshDescription = async () => {
    return insight?.meta[segmentNum].description_short;
  }

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Output />}
        onClick={() => navigate("../export", { relative: "path" })}
      >
        Export
      </Button>
    </Stack>
  );

  return (
    <PageContainer
      actions={actionButtons}
      loading={loading}
      loadingText={"Loading Mindset Insight"}
    >
      {loading ? (
        <MindsetInsightSkeleton />
      ) : (
        <Grid container spacing={2} flexDirection="column" flexGrow={1} pb={8}>
          {error && (
            <Grid>
              <Alert variant="filled" severity="error">
                {error}
              </Alert>
            </Grid>
          )}
          {audience && (
            <>
              {/* Mindset Name */}
              <Grid size={12}>
                <Typography variant="h6">
                  {audience.audience_name}
                </Typography>
              </Grid>

              {/* Segment Cards */}
              <Grid container size={12} spacing={1}>
                {audience.segments.map((segment, index) => (
                  <Grid key={index}>
                    <MindsetSegmentCard
                      segment={segment}
                      onSelect={handleSegmentSelect}
                      selected={segment.segment_id === selectedSegment.segment_id}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Segment Insight */}
              {insight &&
                <InsightLayout
                  tab={mindsetInsightTab}
                  title={selectedSegment.segment_name}
                  summary={audience.audience_size && audience.audience_size[segmentNum]}
                  onDownload={() => null}
                >
                  <InsightMetaProvider
                    handleUpdateDescription={handleUpdateDescription}
                    handleRefreshDescription={handleRefreshDescription}
                  >
                    <MindsetPenPortraitView
                      mindset={selectedSegment}
                      meta={insight.meta[segmentNum]}
                      insights={insight.insights[segmentNum]}
                      pen_portrait={insight.pen_portraits[segmentNum]}
                      locations={insight.locations[segmentNum]}
                      editable={false}
                    />
                  </InsightMetaProvider>
                </InsightLayout>
              }
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default MindsetInsightPage;
